'use client'

import { FormButton, FormGroup, FormInput } from '@/components/form'
import { authActions } from '@/lib/actions/auth'
import { Turnstile } from '@marsidev/react-turnstile'
import { sendGTMEvent } from '@next/third-parties/google'
import { Form, Formik } from 'formik'
import { useRouter } from 'next/navigation'
import { useState, useRef } from 'react'
import * as Yup from 'yup'

const WEB_URL = process.env.NEXT_PUBLIC_SITE_URL
// Use Cloudflare's test key for localhost, production key otherwise
const TURNSTILE_SITE_KEY =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? '1x00000000000000000000AA' // Cloudflare test key (always passes)
    : '0x4AAAAAACSR-BU2Cm_M_rSH'

// Define validation schema with Yup
const validationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
})

const SignUpForm = ({
  buttonLabel = 'Get 5 Free Issues',
  className = '',
  onSuccess,
  mode = 'signup', // 'signup' or 'login'
  trackingLocation = 'unknown',
}) => {
  const [captchaToken, setCaptchaToken] = useState()
  const [formStatus, setFormStatus] = useState(null)
  const turnstileRef = useRef(null)
  const router = useRouter()

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Determine redirect URL based on mode
      const redirectUrl =
        mode === 'signup'
          ? `${WEB_URL}/auth/callback?next=/checkout`
          : `${WEB_URL}/auth/callback`

      // Send OTP with appropriate redirect
      const { data, error } = await authActions.signInWithOtp(
        values.email,
        redirectUrl,
        { captchaToken: captchaToken }
      )

      if (error) {
        throw error
      }

      // ✅ Track: signup_initiated or login_initiated with tracking_location
      sendGTMEvent({
        event: mode === 'signup' ? 'signup_initiated' : 'login_initiated',
        email_domain: values.email.split('@')[1],
        tracking_location: trackingLocation,
      })

      // Call onSuccess callback if provided (to close modal)
      if (onSuccess) {
        onSuccess()
      }

      // Redirect to confirm email page with email and mode parameters
      router.push(`/confirm-email?email=${encodeURIComponent(values.email)}&mode=${mode}`)

      // Reset the form after successful submission
      resetForm()
    } catch (error) {
      console.error('Auth error:', error)

      if (error.message.includes('Email rate limit exceeded')) {
        setFormStatus(
          'Too many requests. Please wait a moment before trying again.'
        )
      } else if (error.message.includes('Invalid email')) {
        setFormStatus('Please enter a valid email address.')
      } else if (error.message.includes('Captcha verification failed')) {
        setFormStatus('Captcha verification failed. Please try again.')
      } else {
        setFormStatus('Something went wrong. Please try again.')
      }

      // Clear error message after 5 seconds
      setTimeout(() => setFormStatus(null), 5000)

      // Reset captcha for retry
      setCaptchaToken(undefined)
      turnstileRef.current?.reset()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={className}>
      {formStatus && (
        <div className="mb-4 p-4 rounded-md text-center bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
          {formStatus}
        </div>
      )}

      <Formik
        initialValues={{ email: '' }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <FormGroup>
              <FormInput
                name="email"
                type="email"
                label="Email"
                placeholder="Enter your email"
                autoComplete="email"
                required
              />

              <FormButton
                type="submit"
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Sending...' : buttonLabel}
              </FormButton>

              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                options={{ size: 'invisible' }}
                onSuccess={(token) => {
                  setCaptchaToken(token)
                }}
                onExpire={() => {
                  setCaptchaToken(undefined)
                  turnstileRef.current?.reset()
                }}
              />
            </FormGroup>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default SignUpForm
